import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CloseOutlined, MinusCircleOutlined } from '@ant-design/icons';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import CustomSpinner from '../../../common/CustomSpinner';
import TextInput from '../../common/TextInput';
import FileUpload from '../../components/UploadFile';
import Button from '../../common/Button';
import Layout from '../../components/Layout';
import { CREATE_ORDER_VALUES, VIN_NAMES, buildCreateOrderPayload, mapOrderToCreateOrderValues } from '../../container/utils/data';
import { validateOrderInputs } from '../../container/utils/validations';
import OrderPlacedModal from '../../components/OrderPlacedModal';
import ExitConfirmationModal from '../../components/ExitConfirmationModal';
import { useNavigate, useParams } from 'react-router-dom';
import Utils from '../../../utils';
import { useDeleteOrder, useGetAttributes, useGetOrder, useUpdateOrder } from '../../container/hooks/useOrders';
import { useDispatch, useSelector } from 'react-redux';
import { translate } from '../../../helper';
import LocalStorage from '../../../defined/localStorage';
import { UserAction } from '../../../redux/action';
import Scanner from './Scanner';
import { Tooltip } from 'antd';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { resolveCssVarValue } from '../../../utils/CommonFunction';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
const initialValues = {
    ...CREATE_ORDER_VALUES.reduce((acc, curr) => {
        if (curr.type === 'file') return acc;
        acc[curr.name] = '';
        return acc;
    }, {}),
    parts: [{ description: '', quantity: '' }]
};

const requiredValues = CREATE_ORDER_VALUES.filter((e) => e.required).map((e) => e.name);

const VIN_MESSAGES = {
    length: 'VIN must be 17 characters',
    invalid: 'Invalid VIN',
    verifying: 'Verifying VIN...',
    valid: 'Valid VIN'
};


function CreateOrder(props) {
    const { newData } = props;
    let orderValues = useSelector((state) => state?.User?.order_details);
    const { data: userDetail } = useSelector((state) => state.auth);
    const [files, setFiles] = useState([]); // {id, file, previewUrl, uploading, path}
    const [imageUrls, setImageUrls] = useState([]); // For new images
    const [isProcessingImages, setIsProcessingImages] = useState(false);
    const [fileUrls, setFileUrls] = useState([]); // For old images (edit mode)
    const [values, setValues] = useState({ ...initialValues });
    const [validation, setValidation] = useState({ ...initialValues });
    const [uploadKey, setUploadKey] = useState(0);
    const [open, setOpen] = useState(false);
    const [dynamicAttributes, setDynamicAttributes] = useState([]);
    const [config, setConfig] = useState({});
    const [deliveryType, setDeliveryType] = useState('');
    const navigate = useNavigate();
    const vinFieldRef = useRef(null);
    const dispatch = useDispatch();
    const { id } = useParams();
    const [scan, setScan] = useState(false);
    const [showExitModal, setShowExitModal] = useState(false);
    const [vinVerificationState, setVinVerificationState] = useState({});
    const [activeVinField, setActiveVinField] = useState(null);
    const vinVerificationRequestRef = useRef({});
    const endpointType = props.dt ? 'dealerTechnicianEndpoints' : 'userMobileEndpoints';
    const { deleteOrder, } = useDeleteOrder(onSuccessDelete, null, endpointType);
    const { getAttributes, isGettingAttributes } = useGetAttributes(onSuccessGettingAttributes, null, endpointType);
    const { updateOrder, isUpdatingOrder } = useUpdateOrder(onSuccessUpdate, null, endpointType);
    const genOrderQuoteUrl = (id) => `/${props.dt ? 'dealer-technician' : 'customer'}/order-quote/${id}`;
    const { getOrderDetails, isLoading } = useGetOrder(
            onGetOrder,
            onGetOrderError,
            endpointType
        );
    const anyUploading = useMemo(
        () => files.some(item => item.uploading),
        [files]
    );

    const isVinVerifying = Object.values(vinVerificationState || {}).some(
        (item) => item?.status === "verifying"
    );

    function onSuccessUpdate(data) {
        Utils.showAlert(1, data.message || 'Order Updated!!');
        navigate(genOrderQuoteUrl(id));
    }

    const onDelete = () => {
        deleteOrder({ id });
    };
    const handlePartChange = (idx, key, value) => {
        const updatedParts = values.parts.map((part, i) =>
            i === idx ? { ...part, [key]: value } : part
        );
        setValues({ ...values, parts: updatedParts });
        const partsErrors = validateParts(updatedParts);
        setValidation((prev) => ({
            ...prev,
            parts: partsErrors
        }));
    };

    const addPart = useCallback(() => {
        setValues({ ...values, parts: [...values.parts, { description: '', quantity: '' }] });
    }, [values.parts]);

    const removePart = useCallback((idx) => {
        setValues({ ...values, parts: values.parts.filter((_, i) => i !== idx) });
    }, [values.parts]);
    function validateParts(parts) {
        const errors = [];
        parts.forEach((part, idx) => {
            if (!part.description || part.description.trim() === '' || !part.quantity && isNaN(part.quantity) || Number(part.quantity) <= 0) {
                errors[idx] = { description: 'These fields are mandatory' };
            } else {
                errors[idx] = {};
            }
        });
        return errors;
    }
    function onSuccessGettingAttributes(data) {
        const type = props.dt ? 2 : 1;
        data = data?.find((e) => e?.order_form_type === type) || {};
        let dynamicValues = data?.dynamic_attribute_names?.map((e) => ({ ...e, value: '' })) || [];
        if (!orderValues?.dynamic_attributes) {
            setDynamicAttributes(dynamicValues);
        }
        setConfig({
            backgroundColor: data?.background_color,
            currencyType: data?.currency_type,
            fixedDisplayNames: data?.fixed_attribute_display_names || {}
        });
        dispatch(UserAction.saveOrderConfigs());
    }

    const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/;
    const VIN_EXTRACT_REGEX = /[A-HJ-NPR-Z0-9]{17}/;

    const sanitizeVinText = (value = '') => value
        .toUpperCase().replace(/vin/gi, "").replace(/[^A-Z0-9]/g, "");

    const normalizeVinValue = (value = '') => sanitizeVinText(value).slice(0, 17);

    function extractVIN(text) {
        if (!text) return null;
        const cleaned = sanitizeVinText(text);
        const match = cleaned.match(VIN_EXTRACT_REGEX);
        return match ? match[0] : null;
    }

    function isValidVinFormat(vin) {
        return VIN_REGEX.test(vin);
    }

    async function validateVINWithAPI(vin) {
        try {
            const res = await fetch(
                `https://vpic.nhtsa.dot.gov/api/vehicles/decodevin/${vin}?format=json`
            );

            const data = await res.json();

            const errorCode = data?.Results?.find(
                (item) => item.Variable === "Error Code"
            )?.Value;

            return String(errorCode).trim() === "0";
        } catch (e) {
            console.error("VIN validation failed", e);
            return false;
        }
    }

    const isVinName = (name) => VIN_NAMES.includes(name.toLowerCase());

    const verifyVinForField = useCallback(async (fieldName, vin) => {
        const requestId = (vinVerificationRequestRef.current[fieldName] || 0) + 1;
        vinVerificationRequestRef.current[fieldName] = requestId;

        setVinVerificationState((prev) => ({
            ...prev,
            [fieldName]: { status: 'verifying', vin: '' }
        }));

        setValidation((prev) => ({
            ...prev,
            [fieldName]: ''
        }));

        const isValid = await validateVINWithAPI(vin);

        if (vinVerificationRequestRef.current[fieldName] !== requestId) {
            return false;
        }

        setVinVerificationState((prev) => ({
            ...prev,
            [fieldName]: {
                status: isValid ? 'valid' : 'invalid',
                vin
            }
        }));

        setValidation((prev) => ({
            ...prev,
            [fieldName]: isValid ? '' : VIN_MESSAGES.invalid
        }));

        return isValid;
    }, []);

    const getCurrentVinValue = useCallback((fieldName, source = 'fixed') => {
        if (source === 'dynamic') {
            const field = dynamicAttributes?.find((attr) => attr?.name === fieldName);
            return normalizeVinValue(field?.value || '');
        }
        return normalizeVinValue(values?.[fieldName] || '');
    }, [dynamicAttributes, values]);

    const setVinFieldValue = useCallback((fieldName, rawValue, source = 'fixed') => {
        const normalizedVin = normalizeVinValue(rawValue);
        const previousVin = getCurrentVinValue(fieldName, source);

        if (normalizedVin === previousVin) {
            return;
        }

        if (source === 'dynamic') {
            setDynamicAttributes((prev) => prev.map((attr) => (
                attr.name === fieldName ? { ...attr, value: normalizedVin } : attr
            )));
        } else {
            setValues((prev) => ({
                ...prev,
                [fieldName]: normalizedVin
            }));
        }

        if (!normalizedVin.length) {
            setValidation((prev) => ({
                ...prev,
                [fieldName]: ''
            }));
            setVinVerificationState((prev) => ({
                ...prev,
                [fieldName]: { status: 'idle', vin: '' }
            }));
            return;
        }

        if (normalizedVin.length < 17) {
            setValidation((prev) => ({
                ...prev,
                [fieldName]: VIN_MESSAGES.length
            }));
            setVinVerificationState((prev) => ({
                ...prev,
                [fieldName]: { status: 'idle', vin: '' }
            }));
            return;
        }

        if (!isValidVinFormat(normalizedVin)) {
            setValidation((prev) => ({
                ...prev,
                [fieldName]: VIN_MESSAGES.invalid
            }));
            setVinVerificationState((prev) => ({
                ...prev,
                [fieldName]: { status: 'invalid', vin: normalizedVin }
            }));
            return;
        }

        verifyVinForField(fieldName, normalizedVin);
    }, [getCurrentVinValue, verifyVinForField]);

    const getVinTargetField = useCallback(() => {
        const hasActiveFixedField = activeVinField?.source === 'fixed' && activeVinField?.fieldName && Object.prototype.hasOwnProperty.call(values, activeVinField.fieldName);
        const hasActiveDynamicField = activeVinField?.source === 'dynamic' && dynamicAttributes?.some((attr) => attr?.name === activeVinField.fieldName);

        if (hasActiveFixedField || hasActiveDynamicField) {
            return activeVinField;
        }

        const fixedField = Object.keys(values).find((name) => isVinName(name));
        if (fixedField) {
            return { fieldName: fixedField, source: 'fixed' };
        }

        const dynamicField = dynamicAttributes?.find((attr) => isVinName(attr?.name));
        if (dynamicField) {
            return { fieldName: dynamicField.name, source: 'dynamic' };
        }

        return null;
    }, [activeVinField, dynamicAttributes, values]);

    const getVinEntriesForSubmit = useCallback(() => {
        const fixedEntries = Object.keys(values)
            .filter((fieldName) => isVinName(fieldName))
            .map((fieldName) => ({
                fieldName,
                vin: normalizeVinValue(values[fieldName] || '')
            }));

        const dynamicEntries = (dynamicAttributes || [])
            .filter((attr) => isVinName(attr?.name))
            .map((attr) => ({
                fieldName: attr.name,
                vin: normalizeVinValue(attr.value || '')
            }));

        return [...fixedEntries, ...dynamicEntries];
    }, [dynamicAttributes, values]);

    const validateVinBeforeSubmit = useCallback(async () => {
        const entries = getVinEntriesForSubmit().filter((entry) => entry.vin);
        if (!entries.length) return true;

        const vinErrors = {};
        const toVerify = [];

        entries.forEach(({ fieldName, vin }) => {
            if (vin.length !== 17) {
                vinErrors[fieldName] = VIN_MESSAGES.length;
                return;
            }

            if (!isValidVinFormat(vin)) {
                vinErrors[fieldName] = VIN_MESSAGES.invalid;
                return;
            }

            const status = vinVerificationState?.[fieldName];
            if (status?.status === 'valid' && status?.vin === vin) {
                return;
            }

            if (status?.status === 'invalid' && status?.vin === vin) {
                vinErrors[fieldName] = VIN_MESSAGES.invalid;
                return;
            }

            toVerify.push({ fieldName, vin });
        });

        if (Object.keys(vinErrors).length) {
            setValidation((prev) => ({
                ...prev,
                ...vinErrors
            }));
            return false;
        }

        const verificationResults = await Promise.all(
            toVerify.map(async ({ fieldName, vin }) => {
                const isValid = await verifyVinForField(fieldName, vin);
                return { fieldName, isValid };
            })
        );

        const invalidVinErrors = verificationResults.reduce((acc, curr) => {
            if (!curr.isValid) {
                acc[curr.fieldName] = VIN_MESSAGES.invalid;
            }
            return acc;
        }, {});

        if (Object.keys(invalidVinErrors).length) {
            setValidation((prev) => ({
                ...prev,
                ...invalidVinErrors
            }));
            return false;
        }

        return true;
    }, [getVinEntriesForSubmit, vinVerificationState, verifyVinForField]);

    const onChangeDynamicValues = useCallback((val, name) => {
        if (isVinName(name)) {
            setVinFieldValue(name, val, 'dynamic');
            return;
        }

        setDynamicAttributes((prev) => prev.map((e) => (e.name === name ? { ...e, value: val } : e)));
        setValidation((prev) => ({
            ...prev,
            [name]: ''
        }));
    }, [setVinFieldValue]);

    const handleVinFocus = (fieldName, source) => {
        setActiveVinField({ fieldName, source });
    };

    function onGetOrder(data) {
        let newData = mapOrderToCreateOrderValues(initialValues, data);
        newData.parts = Array.isArray(data.parts) && data.parts.length > 0
            ? data.parts.map(part => ({
                ...part,
                quantity: part.quantity ? String(part.quantity) : '',
                description: part.description || ''
            }))
            : [{ description: '', quantity: '' }];
        let newDynamicAttributes = Object.keys(data?.dynamic_attributes || {}).map((e, i) => ({
            id: i,
            name: e,
            value: data?.dynamic_attributes?.[e]
        }));
        setDynamicAttributes(newDynamicAttributes);
        setValues(newData);
        setDeliveryType(data?.delivery_method);
        setFileUrls(data?.images || []);
        
        // Verify VIN values from the loaded order
        const restoredVinEntries = [];
        const vin = normalizeVinValue(newData?.vin || '');
        if (vin) {
            restoredVinEntries.push({ fieldName: 'vin', vin });
        }
        // Also collect VIN fields from dynamic attributes
        if (Object.keys(data?.dynamic_attributes || {})?.length > 0) {
            Object.keys(data.dynamic_attributes).forEach((key) => {
                if (isVinName(key)) {
                    const dynamicVin = normalizeVinValue(data.dynamic_attributes[key] || '');
                    if (dynamicVin) {
                        restoredVinEntries.push({ fieldName: key, vin: dynamicVin });
                    }
                }
            });
        }
        // Verify each VIN field
        restoredVinEntries.forEach(({ fieldName, vin }) => {
            verifyVinForField(fieldName, vin);
        });
    }

    function onGetOrderError(data) {
    }

    function onSuccessDelete(data) {
        Utils.showAlert(1, 'Order successfully deleted');
        setTimeout(() => {
            navigate(Utils.EndPoint[endpointType].createOrder);
        }, 500);
    }

    const handleClearImages = () => {
        dispatch(UserAction.resetOrderDetails());
    };

    const submitUpdatedOrder = async (id) => {
        const errors = validateOrderInputs(values, initialValues, requiredValues);
        const partsErrors = validateParts(values.parts);
        const hasPartsError = partsErrors.some(err => Object.keys(err).length > 0);
        if (Object.values(errors).filter((e) => e.length !== 0).length !== 0 || hasPartsError) {
            setValidation({
                ...errors,
                parts: partsErrors
            });
            return;
        }

        await validateVinBeforeSubmit();

        let dynamicAttributesObject = null;
        if (Array.isArray(dynamicAttributes)) {
            const filtered = dynamicAttributes
                .filter(attr => attr && attr.name && attr.value !== undefined)
                .map(attr => [attr.name, attr.value]);
            if (filtered.length > 0) {
                dynamicAttributesObject = Object.fromEntries(filtered);
            }
        }
        const partsWithNumberQuantity = values.parts.map(part => ({
            description: part.description,
            id: part.id || null,
            quantity: part.quantity ? Number(part.quantity) : ''
        }));
        const orderData = { ...values, parts: partsWithNumberQuantity };
        const totalImages = fileUrls.length + files.length;
        if (totalImages > 5) {
            Utils.showAlert(2, translate('maxPhotosExceeded'));
            return;
        }
        // Build payload with all fields
        const payload = buildCreateOrderPayload(orderData, { stringifyParts: true });
        if (dynamicAttributesObject) {
            payload['dynamic_attributes'] = dynamicAttributesObject;
        }
        const oldImageIds = fileUrls.map((img) => img.id);
        payload['oldImages'] = oldImageIds;
        payload['image_urls'] = imageUrls;
        updateOrder({ data: payload, id });
    };

    useEffect(() => {
        if (id) {
            getOrderDetails({ id: id });
        }
        if (!id) {
            getAttributes();
        }
    }, [id]);

    React.useEffect(() => {
        if (userDetail) {
            const {
                user: { profile }
            } = userDetail;
            setValues((prev) => ({
                ...prev,
                shipping_address: `${profile?.address}, ${profile?.city} ${profile?.state}, ${profile?.zip_code}`
            }));
        }
    }, [userDetail]);

    useEffect(() => {
        if (newData && !orderValues) {
            setValues((prev) => ({
                ...prev,
                ...mapOrderToCreateOrderValues(initialValues, newData?.orderData || {}),
                parts: newData?.orderData?.parts || prev.parts
            }));
            setFiles([...(newData.images || [])]);
            setDynamicAttributes([
                ...Object.keys(newData?.dynamic_attributes || {}).map((e) => ({
                    name: e,
                    value: newData?.dynamic_attributes?.[e]
                }))
            ]);
        }
    }, [newData]);

    useEffect(() => {
        if (orderValues && Object.keys(orderValues || {})?.length > 0) {
            handleSavedOrder(orderValues);
        }
    }, [orderValues]);

    function uploadFile(e, type) {
        const inputEl = e?.target;
        if (!inputEl || !inputEl.files) return;
        handleUploadImages(inputEl.files);
        setValidation((prev) => ({
            ...prev,
            file_upload: ''
        }));
        inputEl.value = '';
        setUploadKey((k) => k + 1);
    }

    const handleUploadImages = async (selectedFiles) => {
        // Check total images (old + uploading + new)
        const totalExisting = fileUrls.length + files.filter(f => !f.uploading).length;
        const remainingSlots = 5 - totalExisting;
        if (remainingSlots <= 0) {
            Utils.showAlert(2, translate('maxPhotosExceeded'));
            return;
        }
        const allowedTypes = {
            "image/jpeg": "jpeg",
            "image/jpg": "jpg",
            "image/png": "png",
            "image/gif": "gif",
            "image/webp": "webp",
            "image/heic": "heic",
            "image/heif": "heif",
        };
        const maxImageSize = 20 * 1024 * 1024;
        let validFiles = Array.from(selectedFiles).filter((file) => {
            if (!allowedTypes[file.type]) {
                Utils.showAlert(2, `File type not allowed: ${file.name}`);
                return false;
            }
            if (file.size > maxImageSize) {
                Utils.showAlert(2, `Image size exceeds 20MB: ${file.name}`);
                return false;
            }
            return true;
        });
        if (validFiles.length === 0) return;
        validFiles = validFiles.slice(0, remainingSlots);
        if (selectedFiles.length > remainingSlots) {
            Utils.showAlert(2, translate('maxPhotosExceeded'));
        }
        // Add to files state with uploading: true and previewUrl
        const newItems = validFiles.map((file, i) => {
            const id = `${Date.now()}-${Math.random()}-${i}`;
            return {
                id,
                file,
                previewUrl: URL.createObjectURL(file),
                uploading: true,
                path: null,
            };
        });
        setFiles((prev) => [...prev, ...newItems]);

        // Prepare payload for uploadFileApi
        const contentTypes = validFiles.map((file) => file.type);
        let uploadUrls = [];
        try {
            const payload = { content_type: contentTypes };
            uploadUrls = await new Promise((resolve, reject) => {
                Utils.api.postApiCall(
                    Utils.EndPoint.vendorEndpoints.uploadFileApi,
                    payload,
                    (res) => resolve(res.data.upload_urls),
                    (err) => reject(err)
                );
            });
        } catch (error) {
            Utils.showAlert(2, `Failed to fetch upload URLs. Please try again.`);
            // Remove uploading files from preview
            setFiles((prev) => prev.filter((item) => !newItems.some((n) => n.id === item.id)));
            return;
        }

        // Upload all file parallel to its upload_url- Parallelize uploads
        await Promise.all(
            validFiles.map(async (file, i) => {
                const urlObj = uploadUrls[i];
                const itemId = newItems[i].id;

                try {
                    const res = await fetch(urlObj.upload_url, {
                        method: 'PUT',
                        body: file,
                        headers: { 'x-ms-blob-type': 'BlockBlob' },
                    });

                    if (res.status === 201) {
                        setFiles(prev =>
                            prev.map(item =>
                                item.id === itemId
                                    ? { ...item, uploading: false, path: urlObj.file_path }
                                    : item
                            )
                        );
                        setImageUrls(prev => [...prev, urlObj.file_path]);
                    } else {
                        throw new Error();
                    }
                } catch {
                    setFiles(prev => prev.filter(item => item.id !== itemId));
                }
            })
        );
    };

    // Camera image upload: treat like file upload
    const onCamUpload = (data) => {
        handleUploadImages([data]);
        setUploadKey((k) => k + 1);
    };


    const onChange = useCallback((value, key) => {
        if (isVinName(key)) {
            setVinFieldValue(key, value, 'fixed');
            return;
        }

        setValidation(prev => ({
            ...prev,
            [key]: ""
        }));

        setValues(prev => ({
            ...prev,
            [key]: value
        }));
    }, [setVinFieldValue]);

    // Not needed, handled in previewUrl

    // Remove uploaded or uploading image
    const deleteFilled = (id) => {
        setFiles((prev) => {
            const fileToRemove = prev.find((e) => e.id === id);
            if (fileToRemove?.path) {
                setImageUrls((urls) => urls.filter((url) => url !== fileToRemove.path));
            }
            return prev.filter((e) => e.id !== id);
        });
        setUploadKey((k) => k + 1);
    };

    // Remove old image (from fileUrls)
    const deleteSavedFiles = (idToRemove) => {
        setFileUrls(prev => prev.filter(file => file.id !== idToRemove));
        setUploadKey((k) => k + 1);
    };
    const submitOrder = async () => {
        const errors = validateOrderInputs(values, initialValues, requiredValues);
        const partsErrors = validateParts(values.parts);
        const hasPartsError = partsErrors.some(err => Object.keys(err).length > 0);
        if (Object.values(errors).filter((e) => e.length !== 0).length !== 0 || hasPartsError) {
            setValidation({
                ...errors,
                parts: partsErrors
            });
            return;
        }
        if (!deliveryType) {
            setValidation({ deliveryType: 'This field is mandatory' });
            return;
        }

        await validateVinBeforeSubmit();

        let mandatDynamic = dynamicAttributes.filter(e => e.mandatory);
        if (mandatDynamic.length > 0 && mandatDynamic.filter(e => !e.value).length > 0) {
            setValidation({
                ...dynamicAttributes.reduce((acc, curr) => {
                    if (curr.mandatory && !curr.value) {
                        acc[curr.name] = 'This field is mandatory';
                    }
                    return acc;
                }, {})
            });
            return;
        }
        // Use imageUrls for API
        const partsWithNumberQuantity = values.parts.map(part => ({
            ...part,
            quantity: part.quantity ? Number(part.quantity) : ''
        }));
        const orderData = {
            ...values,
            delivery_method: deliveryType,
            parts: partsWithNumberQuantity
        };


        const orderDetails = {
            orderData,
            imageUrls: imageUrls, // only URLs
            dynamic_attributes: dynamicAttributes?.reduce((acc, curr) => {
                acc[curr.name] = curr.value;
                return acc;
            }, {})
        };
        const previewImages = [
            ...files
                .filter(f => f.path && f.previewUrl)
                .map(f => ({
                    path: f.path,
                    previewUrl: f.previewUrl,
                    name: f.file?.name || '',
                    id: f.id,
                    file: f
                }))
        ];

        const orderDetailsNew = {
            ...orderDetails, // preserve previous details
            images: previewImages, // for UI preview
            // ...other keys
        };
        dispatch(UserAction.saveOrderDetails(orderDetailsNew))
            .then(() => {
                navigate(Utils.EndPoint[endpointType].vendors, {
                    state: { ...orderDetails, isCreatingOrder: true, isOrdering: true }
                });
            });
    };

    const layoutProps = {
        ...(!id && props.backTo
            ? {
                backTo: () => {
                    props.backTo();
                    LocalStorage.removeItem('newOrder');
                    handleClearImages();
                }
            }
            : {})
    };
    const handleSavedOrder = async () => {
        if (!(orderValues?.orderData || orderValues?.images || orderValues?.dynamic_attributes)) return;
        let {
            orderData = {},
            images,
            imageUrls,
            dynamic_attributes
        } = orderValues || {};
        const restoredValues = mapOrderToCreateOrderValues(initialValues, orderData);
        const restoredParts = Array.isArray(orderData?.parts) && orderData.parts.length > 0
            ? orderData.parts
            : [{ description: '', quantity: '' }];

        setValues((prev) => ({
            ...prev,
            ...restoredValues,
            shipping_address: prev?.shipping_address || restoredValues.shipping_address || '',
            parts: restoredParts,
        }));
        // Verify restored VIN values instead of blindly setting them as valid
        const restoredVinEntries = [];
        const vin = normalizeVinValue(restoredValues?.vin || '');
        if (vin) {
            restoredVinEntries.push({ fieldName: 'vin', vin });
        }
        // Also collect VIN fields from dynamic attributes
        if (Object.keys(dynamic_attributes || {})?.length > 0) {
            Object.keys(dynamic_attributes).forEach((key) => {
                if (isVinName(key)) {
                    const dynamicVin = normalizeVinValue(dynamic_attributes[key] || '');
                    if (dynamicVin) {
                        restoredVinEntries.push({ fieldName: key, vin: dynamicVin });
                    }
                }
            });
        }
        // Verify each restored VIN field
        restoredVinEntries.forEach(({ fieldName, vin }) => {
            verifyVinForField(fieldName, vin);
        });
        setDeliveryType(orderData?.delivery_method);
        if (Object.keys(dynamic_attributes || {})?.length > 0) {
            let data = Object.keys(dynamic_attributes || {}).map((e) => ({
                name: e,
                value: dynamic_attributes[e]
            }));
            setDynamicAttributes(data);
        }
        if (!id) {
            setFiles(images || []);
            setImageUrls(imageUrls || []);
        }
    };

    const deliveryTypes = [
        {
            name: 'instore',
            label: 'inStorePickup'
        },
        {
            name: 'delivery',
            label: 'delivery'
        }
    ];

    const handleDeliveryTypeChange = (e) => {
        setDeliveryType(e.target.value);
        setValidation((prev) => ({
            ...prev,
            deliveryType: ''
        }));
    };

    const handleScan = () => {
        setScan(true);
        vinFieldRef.current?.scrollIntoView({ behavior: 'auto' });
    };

    const handleScannerResponse = async (data) => {
        const identifiedPhrases = data?.data?.identified_phrases;

        const rawText = identifiedPhrases?.length
            ? identifiedPhrases[0]?.text
            : "";

        const vin = extractVIN(rawText);

        const target = getVinTargetField();
        if (!target) {
            return;
        }

        if (!vin) {
            const normalizedRawText = normalizeVinValue(rawText);
            setVinFieldValue(target.fieldName, normalizedRawText || '', target.source);
            return;
        }

        setVinFieldValue(target.fieldName, vin, target.source);
    };

    const getVinStatus = (fieldName) => vinVerificationState?.[fieldName]?.status;

    const renderVinStatusMessage = (fieldName, compact = false) => {
        const vinStatus = getVinStatus(fieldName);

        if (vinStatus === 'verifying') {
            return (
                <p style={{ fontSize: compact ? '12px' : '14px', marginTop: compact ? '4px' : '8px' }}>
                    {VIN_MESSAGES.verifying}
                </p>
            );
        }

        if (vinStatus === 'valid') {
            return (
                <p style={{ fontSize: compact ? '12px' : '14px', marginTop: compact ? '4px' : '8px', color: 'green' }}>
                    {VIN_MESSAGES.valid}
                </p>
            );
        }

        return null;
    };

    function getVinRightElement(status) {
        if (status === "verifying") {
            return <CustomSpinner size={26} top={0} />;
        }

        if (status === "valid") {
            return <CheckCircleIcon sx={{ fontSize: '20px', color: 'green' }} />;
        }

        if (status === "invalid") {
            return <WarningIcon sx={{ fontSize: '20px', color: 'red' }} />;
        }

        return null;
    }

    const handleCloseCRM = () => {
        setScan(false);
    };

    const handleShowExitModal = () => {
        setShowExitModal(true);
    };

    const handleCloseExitModal = () => {
        setShowExitModal(false);
    };

    const handleConfirmExit = () => {
        setShowExitModal(false);
        navigate(-1);
        handleClearImages();
    };

    return (
        <Layout
            showNavigation
            showNotification
            title={id ? 'editOrder' : 'createOrder'}
            backTo={handleShowExitModal}
            isHomePage
            {...layoutProps}>
            <div
                style={{
                    padding: '30px 15px',
                    width: '100%',
                    backgroundColor:
                        config?.backgroundColor || '#FFFFFF',
                    marginTop: '-5%',
                    overflowY: 'auto',
                    scrollBehavior: 'smooth'
                }}>
                {CREATE_ORDER_VALUES
                    .filter((e) => ['order_name', 'shipping_address'].includes(e?.name))
                    .map((value) => {
                        return <TextInput
                            style={value.style}
                            disabled={isGettingAttributes}
                            rows={value.rows}
                            className={value.className}
                            label={config?.fixedDisplayNames?.[value.name] || translate(value.label)}
                            placeholder={config?.fixedDisplayNames?.[value.name] || translate(value.placeholder)}
                            name={value.name}
                            onChange={onChange}
                            value={values[value.name]}
                            key={value.name}
                            error={validation[value.name]}
                            required={value.required}
                            type={value.type}
                        />
                    })}
                <div className="mobile-item-details" style={{ marginBottom: 0 }}>
                    <div
                        className="text-input"
                        style={{
                            fontSize: '14px',
                            fontWeight: '400',
                            color: '#000',
                            marginBottom: '15px'
                        }}
                    >
                        {translate('itemDetails')}*
                    </div>
                    {values.parts.map((part, idx) => (
                        <div key={idx} style={{ display: 'flex', gap: "10px", alignItems: 'flex-start', marginBottom: '10px' }}>
                            <div style={{ flex: 1 }}><TextInput
                                outerStyle={{ flex: 1 }}
                                style={{ background: 'transparent' }}
                                placeholder={translate('descriptionPartNumber')}
                                value={part.description}
                                onChange={val => handlePartChange(idx, 'description', val)}
                                required
                                name={`description_${idx}`}
                            />
                                {validation.parts && validation.parts[idx]?.description && (
                                    <p className="text-input-error">{validation.parts[idx].description}</p>
                                )}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}><select
                                name={`quantity_${idx}`}
                                value={part.quantity}
                                className="custom-white-select"
                                onChange={e => handlePartChange(idx, 'quantity', e.target.value)}
                                style={{
                                    flex: 'none',
                                    width: '80px',
                                    height: '50px',
                                    borderRadius: '8px',
                                    border: '1px solid #D8D8D8',
                                    padding: '0 32px 0 12px',
                                    fontSize: '14px',
                                    backgroundColor: '#fff',
                                    background: `#fff url("data:image/svg+xml;utf8,<svg fill='gray' height='20' viewBox='0 0 24 24' width='20' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>") no-repeat right 8px center/20px 20px`,
                                    outline: 'none',
                                    appearance: 'none',
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'none',
                                    boxSizing: 'border-box',
                                    cursor: 'pointer',
                                    color: part.quantity ? '#000' : '#6b7280'
                                }}
                                required
                            >
                                <option value="" disabled>
                                    {translate('Qty.')}
                                </option>
                                {[...Array(20)].map((_, i) => (
                                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                                ))}
                            </select>
                                {values.parts.length > 1 && (
                                    <MinusCircleOutlined
                                        style={{ color: 'red', fontSize: 20, cursor: 'pointer', marginRight: 8 }}
                                        onClick={() => removePart(idx)}
                                    />
                                )}</div>
                        </div>
                    ))}
                    <Button
                        onClick={addPart}
                        type="secondary"
                        style={{
                            background: '#fff',
                            border: '2px solid #555555',
                            borderRadius: '4px',
                            fontSize: '15px',
                            color: '#000',
                            fontWeight: 600,
                            boxShadow: 'none',
                            minWidth: '120px',
                            padding: '8px 20px',
                            width: 'auto',
                            alignSelf: 'flex-start',
                            marginTop: '16px '
                        }}
                    >
                        {translate('addItem')}
                    </Button>
                </div>

                {CREATE_ORDER_VALUES
                    .filter((e) => !['description', 'quantity', 'file_upload', 'order_name', 'shipping_address'].includes(e?.name))
                    .map((value) => {
                        const vinState = getVinStatus(value.name);
                        const fieldLabel = config?.fixedDisplayNames?.[value.name] || translate(value.label);
                        const vinLabelSuffix = isVinName(value?.name) ? (
                            <Tooltip
                                title={
                                    <div>
                                        <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                            <li>Position only the VIN inside the yellow box.</li>
                                            <li>Hold your phone steady and allow the camera to focus.</li>
                                            <li>If the VIN is positioned vertically or sideways, lock your screen rotation and align the VIN within the yellow box.</li>
                                            <li>Once captured, the VIN will be automatically scanned and validated through NHTSA.</li>
                                        </ul>
                                        <p style={{ margin: '8px 0 0 0' }}><strong>Tip:</strong> Keep all other text outside the yellow box for the most accurate results.</p>
                                    </div>
                                }
                                placement="top"
                                trigger="click"
                                overlayInnerStyle={{
                                    fontSize: '14px',
                                    padding: '8px 12px'
                                }}
                            >
                                <InfoOutlinedIcon
                                    sx={{
                                        fontSize: '16px',
                                        marginLeft: '6px',
                                        color: '#6B7280',
                                        cursor: 'pointer'
                                    }}
                                />
                            </Tooltip>
                        ) : null;
                        return value.type === 'text' || value.type === 'number' ? (
                            <React.Fragment key={value.name}>
                                <div style={isVinName(value.name) ? { display: 'flex', gap: '10px', alignItems: 'flex-end' } : {}}>
                                    <div style={isVinName(value.name) ? { flex: 1 } : {}}>
                                        <TextInput
                                            style={value.style}
                                            disabled={isGettingAttributes}
                                            rows={value.rows}
                                            className={value.className}
                                            label={fieldLabel}
                                            labelSuffix={vinLabelSuffix}
                                            placeholder={config?.fixedDisplayNames?.[value.name] || translate(value.placeholder)}
                                            name={value.name}
                                            onChange={onChange}
                                            value={values[value.name]}
                                            key={value.name}
                                            error={validation[value.name]}
                                            required={value.required}
                                            type={value.type}
                                            onFocus={isVinName(value.name) ? () => handleVinFocus(value.name, 'fixed') : null}
                                            ref={isVinName(value.name) ? vinFieldRef : null}
                                            rightElement={isVinName(value.name) ? getVinRightElement(vinState) : null}
                                        />
                                    </div>
                                    {isVinName(value.name) && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                handleVinFocus(value.name, 'fixed');
                                                handleScan();
                                            }}
                                            style={{
                                                height: '50px',
                                                width: '80px',
                                                borderRadius: '8px',
                                                border: '1px solid #D8D8D8',
                                                backgroundColor: '#fff',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                flexShrink: 0,
                                                gap: '2px'
                                            }}
                                        >
                                            <CameraAltOutlinedIcon sx={{ fontSize: '20px', color: '#555' }} />
                                            <span style={{ fontSize: '10px', color: '#555', fontWeight: 500 }}>Scan</span>
                                        </button>
                                    )}
                                </div>
                                {isVinName(value.name) && renderVinStatusMessage(value.name)}
                                {isVinName(value.name) && scan && (
                                    <Scanner
                                        setScan={setScan}
                                        handleScannerResponse={handleScannerResponse}
                                        handleClose={handleCloseCRM}
                                    />
                                )}
                            </React.Fragment>
                        ) : value.type === 'file' ? (
                            <React.Fragment key={value.name}>
                                {id && (
                                    <>
                                        {
                                            <div
                                                style={{
                                                    fontSize: '14px',
                                                    fontWeight: '400',
                                                    color: '#000',
                                                    marginBottom: '5px'
                                                }}
                                                className="text-input">
                                                {translate('images')}
                                            </div>
                                        }
                                        <div style={{ display: 'flex', overflowY: 'auto' }}>
                                            {fileUrls.map((data) => (
                                                <img
                                                    src={data?.image}
                                                    style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        marginRight: '8px',
                                                        borderRadius: '8px'
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                                <FileUpload
                                    key={`file-upload-${uploadKey}`}
                                    label={translate(value.label)}
                                    onChange={uploadFile}
                                    onCamUpload={onCamUpload}
                                    files={files}
                                    onProcessingChange={setIsProcessingImages}
                                />
                                {validation['file_upload'] && (
                                    <p className="text-input-error">{validation['file_upload']}</p>
                                )}
                            </React.Fragment>
                        ) : (
                            <></>
                        );
                    })}
                {dynamicAttributes?.map((attr) => {
                    const attrLabel = translate(attr.name);
                    const dynamicVinLabelSuffix = isVinName(attr?.name) ? (
                        <Tooltip
                            title={
                                <div>
                                    <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                        <li>Position only the VIN inside the yellow box.</li>
                                        <li>Hold your phone steady and allow the camera to focus.</li>
                                        <li>If the VIN is positioned vertically or sideways, lock your screen rotation and align the VIN within the yellow box.</li>
                                        <li>Once captured, the VIN will be automatically scanned and validated through NHTSA.</li>
                                    </ul>
                                    <p style={{ margin: '8px 0 0 0' }}><strong>Tip:</strong> Keep all other text outside the yellow box for the most accurate results.</p>
                                </div>
                            }
                            placement="top"
                            trigger="click"
                            overlayInnerStyle={{
                                fontSize: '14px',
                                padding: '8px 12px'
                            }}
                        >
                            <InfoOutlinedIcon
                                sx={{
                                    fontSize: '16px',
                                    marginLeft: '6px',
                                    color: '#6B7280',
                                    cursor: 'pointer'
                                }}
                            />
                        </Tooltip>
                    ) : null;
                    return (
                    <React.Fragment key={attr.id}>
                        <div style={isVinName(attr.name) ? { display: 'flex', gap: '10px', alignItems: 'flex-end' } : {}}>
                            <div style={isVinName(attr.name) ? { flex: 1 } : {}}>
                                <TextInput
                                    key={attr.id}
                                    style={attr.style}
                                    disabled={isGettingAttributes}
                                    rows={1}
                                    className={attr.className}
                                    label={attrLabel}
                                    labelSuffix={dynamicVinLabelSuffix}
                                    placeholder={translate(attr.name)}
                                    name={attr.name}
                                    onChange={onChangeDynamicValues}
                                    value={attr.value}
                                    required={attr.mandatory}
                                    error={validation[attr.name]}
                                    onFocus={isVinName(attr.name) ? () => handleVinFocus(attr.name, 'dynamic') : null}
                                    ref={isVinName(attr.name) ? vinFieldRef : null}
                                    rightElement={isVinName(attr.name) ? getVinRightElement(getVinStatus(attr.name)) : null}
                                />
                            </div>
                            {isVinName(attr.name) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        handleVinFocus(attr.name, 'dynamic');
                                        handleScan();
                                    }}
                                    style={{
                                        height: '50px',
                                        width: '80px',
                                        borderRadius: '8px',
                                        border: '1px solid #D8D8D8',
                                        backgroundColor: '#fff',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                        gap: '2px'
                                    }}
                                >
                                    <CameraAltOutlinedIcon sx={{ fontSize: '20px', color: '#555' }} />
                                    <span style={{ fontSize: '10px', color: '#555', fontWeight: 500 }}>Scan</span>
                                </button>
                            )}
                        </div>
                        {isVinName(attr?.name) && renderVinStatusMessage(attr.name, true)}
                        {isVinName(attr?.name) && scan && (
                            <Scanner
                                setScan={setScan}
                                handleScannerResponse={handleScannerResponse}
                                handleClose={handleCloseCRM}
                            />
                        )}
                    </React.Fragment>
                    );
                })}
                <FormControl style={{ marginTop: '20px' }}>
                    <FormLabel
                        style={{ fontSize: '14px', marginBottom: '5px' }}
                        id="demo-radio-buttons-group-label">
                        {translate('deliveryMethod')}*
                    </FormLabel>
                    <RadioGroup
                        aria-labelledby="demo-radio-buttons-group-label"
                        name="radio-buttons-group"
                        value={deliveryType}
                        onChange={handleDeliveryTypeChange}
                        style={{
                            paddingLeft: '20px'
                        }}>
                        {deliveryTypes.map((type, i) => (
                            <FormControlLabel
                                key={type.name}
                                style={{ fontSize: '14px' }}
                                value={type.name}
                                control={<Radio disabled={id ? true : false} size="small" />}
                                label={translate(type.label)}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
                {validation['deliveryType'] && (
                    <p className="text-input-error">{validation['deliveryType']}</p>
                )}
                {CREATE_ORDER_VALUES?.filter((e) => e?.name === 'file_upload').map((value) => {
                    return (
                        <React.Fragment key={value.name}>
                            <FileUpload
                                key={`file-upload-bottom-${uploadKey}`}
                                label={translate(value.label)}
                                onChange={uploadFile}
                                onCamUpload={onCamUpload}
                                files={files}
                                onProcessingChange={setIsProcessingImages}
                            />
                            {validation['file_upload'] && (
                                <p className="text-input-error">{validation['file_upload']}</p>
                            )}
                        </React.Fragment>
                    );
                })}

                {/* Uploaded and uploading images preview */}
                {files.map((file, i) => {
                    return (
                        <div
                            key={file.id}
                            style={{
                                width: '100%',
                                height: '75px',
                                border: '1px solid #D8D8D8',
                                borderRadius: '0.5rem',
                                padding: '10px',
                                position: 'relative',
                                display: 'flex',
                                marginTop: '20px',
                                backgroundColor: '#C8C8C8',
                                opacity: file.uploading ? 0.6 : 1
                            }}>
                            <img
                                src={file.previewUrl}
                                style={{
                                    width: '55px',
                                    height: '55px',
                                    // filter: file.uploading ? 'blur(2px) brightness(0.85)' : 'none'
                                }}
                            />
                            <div
                                style={{
                                    width: 'calc(100% - 70px)',
                                    marginLeft: '15px',
                                    height: '55px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center'
                                }}>
                                <span>{file?.file?.name || file?.name}</span>
                            </div>
                            {file.uploading && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    background: 'rgba(0,0,0,0.7)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '0.5rem',
                                    zIndex: 2
                                }}>
                                    <CustomSpinner size={resolveCssVarValue('--fs-xl') * 2} />
                                </div>
                            )}
                            <CloseOutlined
                                style={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                    zIndex: 3
                                }}
                                onClick={() => deleteFilled(file.id)}
                            />
                        </div>
                    )
                })}

                {fileUrls.map((data) => {
                    const inputName = `image_product_${data?.id}`;
                    return (
                        <div
                            key={data?.id}
                            style={{
                                width: '100%',
                                height: '75px',
                                border: '1px solid #D8D8D8',
                                borderRadius: '0.5rem',
                                padding: '10px',
                                position: 'relative',
                                display: 'flex',
                                marginTop: '20px',
                                backgroundColor: '#C8C8C8'
                            }}
                        >
                            <img
                                src={data?.thumbnail || data?.image}
                                alt={inputName}
                                style={{
                                    width: '55px',
                                    height: '55px'
                                }}
                            />
                            <div
                                style={{
                                    width: 'calc(100% - 70px)',
                                    marginLeft: '15px',
                                    height: '55px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center'
                                }}
                            >
                                <span>{inputName}</span>
                            </div>
                            <CloseOutlined
                                style={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px'
                                }}
                                onClick={() => deleteSavedFiles(data.id)}
                            />
                        </div>
                    );
                })}

                {!id && (
                    <Button
                        onClick={submitOrder}
                        disabled={isProcessingImages || anyUploading || isVinVerifying}
                        style={{ marginBottom: '10px', opacity: (isProcessingImages || anyUploading || isVinVerifying) ? 0.6 : 1, cursor: (isProcessingImages || anyUploading || isVinVerifying) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {(isProcessingImages || anyUploading) ? (
                            <>
                                <CustomSpinner size={resolveCssVarValue('--fs-xl') * 1.32075} />
                                <span style={{ fontWeight: 'bold' }}>{translate('uploadingImages')}</span>
                            </>
                        ) : (
                            translate('sendToLocation')
                        )}
                    </Button>
                )}

                {id && (
                    <Button
                        onClick={() => submitUpdatedOrder(id)}
                        disabled={isUpdatingOrder || anyUploading || isVinVerifying}
                        style={{
                            marginBottom: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            opacity: (isUpdatingOrder || anyUploading || isVinVerifying) ? 0.6 : 1,
                            cursor: (isUpdatingOrder || anyUploading || isVinVerifying) ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {isUpdatingOrder ? (
                            <>
                                <CustomSpinner size={resolveCssVarValue('--fs-xl') * 1.32075} />
                                <span style={{ fontWeight: 'bold' }}>{translate('saving')}...</span>
                            </>
                        ) : (
                            <span style={{ fontWeight: 'bold' }}>{translate('saveOrder')}</span>
                        )}
                    </Button>
                )}

                {id && (
                    <Button
                        onClick={() => setOpen(true)}
                        style={{
                            marginBottom: '100px',
                            background: '#fff',
                            color: '#6B7280',
                            border: '1px solid #6B7280',
                            boxShadow: 'none'
                        }}>
                        {translate('deleteOrder')}
                    </Button>
                )}
            </div>
            {isLoading && <CustomSpinner fullPage top={0} />}
            <OrderPlacedModal
                open={open}
                onClose={() => setOpen(!open)}
                mode={'Warning'}
                handleSubmit={onDelete}
            />
            <ExitConfirmationModal
                open={showExitModal}
                onClose={handleCloseExitModal}
                onConfirm={handleConfirmExit}
            />
        </Layout>
    );
}

export default CreateOrder;
